import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Shield, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';

const RegisterPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    const result = await register(email, password, name);
    
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-8">
            <Shield className="w-10 h-10 text-[#0066FF]" />
            <span className="text-2xl font-bold text-[#F9FAFB] tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Agent Control Plane
            </span>
          </Link>
          <h1 className="text-3xl font-bold text-[#F9FAFB] mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Request Pilot Access
          </h1>
          <p className="text-[#9CA3AF]">Start securing your AI agents today</p>
        </div>

        <div className="enterprise-card p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-[#FF3B30]/10 border border-[#FF3B30]/50 text-[#FF3B30] px-4 py-3 text-sm" data-testid="register-error">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="name" className="text-[#9CA3AF] text-sm uppercase tracking-wider">
                Full Name
              </Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="bg-[#1A1A1A] border-[#27272A] text-[#F9FAFB] rounded-none focus:border-[#0066FF] focus:ring-0"
                placeholder="John Smith"
                required
                data-testid="register-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#9CA3AF] text-sm uppercase tracking-wider">
                Work Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-[#1A1A1A] border-[#27272A] text-[#F9FAFB] rounded-none focus:border-[#0066FF] focus:ring-0"
                placeholder="you@company.com"
                required
                data-testid="register-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#9CA3AF] text-sm uppercase tracking-wider">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-[#1A1A1A] border-[#27272A] text-[#F9FAFB] rounded-none focus:border-[#0066FF] focus:ring-0 pr-10"
                  placeholder="Minimum 6 characters"
                  required
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B7280] hover:text-[#9CA3AF]"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full primary-btn py-6"
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? 'Creating account...' : 'Request Access'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-[#6B7280] text-sm">
              Already have an account?{' '}
              <Link to="/login" className="text-[#0066FF] hover:text-[#3385FF]" data-testid="register-login-link">
                Sign in
              </Link>
            </p>
          </div>
        </div>

        <div className="mt-8 text-center">
          <Link to="/" className="text-[#6B7280] text-sm hover:text-[#9CA3AF]">
            &larr; Back to home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
